import { z } from "zod";
import { siteLocaleCodes } from "./site-languages";
import type { WebsiteImportRecord } from "./website-import";
import { MAX_SITE_PAGES } from "./navigation-options";

export const suggestedPageIds = ["services", "products", "about", "team", "process", "work", "gallery", "faq", "contact"] as const;
export type SuggestedPage = typeof suggestedPageIds[number];

/** Explicit selections, not the page inventory detected on an old website. */
export const customBrandPageSchema = z.object({
  id: z.string().uuid(), title: z.string().trim().min(1).max(100),
  slug: z.string().trim().min(1).max(100).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .refine(value => ![...siteLocaleCodes, "api", "s", "privacy-policy", "terms-of-use", "cookie-policy"].includes(value), "This path is reserved"),
  template: z.enum(["blank", ...suggestedPageIds, "custom"]).default("custom"),
  summary: z.string().trim().max(900).default(""),
  showInNavigation: z.boolean().default(true), showInFooter: z.boolean().default(false),
  navigationParentSlug: z.string().max(100).nullable().optional(),
  navigationParentId: z.string().max(100).nullable().optional(), sitePageId: z.string().max(80).optional()
}).strict();
export type CustomBrandPage = z.infer<typeof customBrandPageSchema>;
export const customBrandPagesSchema = z.array(customBrandPageSchema).max(MAX_SITE_PAGES - 1).default([])
  .refine(pages => new Set(pages.map(page => page.id)).size === pages.length && new Set(pages.map(page => page.slug)).size === pages.length, "Custom pages need unique IDs and paths");

export function selectedBrandPages(brand: { starterPages: SuggestedPage[]; customPages: CustomBrandPage[]; websiteImport: WebsiteImportRecord | null; pageOrder?: string[] }) {
  const imported = brand.websiteImport?.pages || [], custom = brand.customPages || [];
  const selected = imported.filter(page => page.slug && brand.starterPages.includes(page.template as SuggestedPage))
    .map(page => ({ ...page, imported: true }));
  for (const template of brand.starterPages) {
    if (selected.some(page => page.template === template) || selected.some(page => page.slug === template)) continue;
    selected.push({ sourceUrl: "", slug: template, title: "", template, summary: "", showInNavigation: true, showInFooter: true, imported: false });
  }
  for (const choice of custom) {
    if (selected.some(page => page.slug === choice.slug)) continue;
    const source = imported.find(page => page.slug === choice.slug);
    selected.push({ sourceUrl: "", ...source, ...choice, imported: Boolean(source) });
  }
  const order = brand.pageOrder || [];
  return selected.sort((a, b) => {
    const rank = (page: typeof a) => order.includes(page.slug) ? order.indexOf(page.slug)
      : order.length + (suggestedPageIds.includes(page.template as SuggestedPage) ? suggestedPageIds.indexOf(page.template as SuggestedPage) : suggestedPageIds.length);
    return rank(a) - rank(b);
  });
}
